import * as s from "@gromia/components/theme/t.module.css";
import { Modal } from "@gromia/components/ui/Modal";
import { useClickOutside } from "@gromia/utils/useClickOutside";
import { useMedia } from "@gromia/utils/useMedia";
import cx from "classnames";
import { pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import React from "react";
import { usePopper } from "react-popper";

export type TriggerProps = {
  triggerRef?: React.Ref<any>;
  onClick?: () => void;
  onMouseEnter?: () => void;
  children: React.ReactNode;
};

export type Trigger = React.FunctionComponent<TriggerProps>;
export type TSuper = React.ReactElement<TriggerProps>;

export type DropdownProps<T> = {
  trigger: T;
  label: NonNullable<React.ReactNode>;
  minWidth?: number;
  className?: string;
  closeNode?: (opts: { onClose: () => void }) => NonNullable<React.ReactNode>;
  contentNode: (opts: { onClose: () => void }) => NonNullable<React.ReactNode>;
  placement?: "bottom" | "top";
  defaultIsOpen?: boolean;
  inline?: boolean;
  inlineLabel?: NonNullable<React.ReactNode>;
};

export function DropdownDesktop<T extends TSuper>(props: DropdownProps<T>) {
  const [isOpen, setIsOpen] = React.useState(props.defaultIsOpen);
  const referenceRef = React.useRef(null);
  const popperRef = React.useRef(null);
  const arrowRef = React.useRef(null);
  const p = usePopper(referenceRef.current, popperRef.current, {
    placement: props.placement,
    modifiers: [
      { name: "arrow", options: { element: arrowRef.current } },
      {
        name: "offset",
        options: {
          offset: [0, 15],
        },
      },
    ],
  });

  React.useEffect(() => {
    if (p.forceUpdate) {
      p.forceUpdate();
    }
  }, [isOpen]);

  function onClose() {
    setIsOpen(false);
  }

  function toggleIsOpen() {
    setIsOpen(!isOpen);
  }

  const wrapperRef = useClickOutside(onClose);

  return (
    <span ref={wrapperRef} className={props.className}>
      {React.cloneElement(props.trigger, {
        triggerRef: referenceRef,
        onClick: toggleIsOpen,
      })}
      <div
        ref={popperRef}
        style={p.styles.popper}
        className={cx(
          s.rounded,
          s.bgWhite,
          s.pt2,
          s.pb2,
          s.shadowLg,
          s.popperContent,
          s.zIndex2,
          isOpen ? s.dBlock : s.dNone,
        )}
        {...p.attributes.popper}
      >
        {props.closeNode ? props.closeNode({ onClose }) : null}
        {props.contentNode({ onClose })}
        <div
          ref={arrowRef}
          className={cx(s.popperArrow)}
          style={p.styles.arrow}
          x-placement={props.placement}
        />
      </div>
    </span>
  );
}

function DropdownMobile<T extends TSuper>(props: DropdownProps<T>) {
  return (
    <Modal
      defaultIsOpen={props.defaultIsOpen}
      trigger={({ onModalOpen }) =>
        React.cloneElement(props.trigger, {
          triggerRef: React.createRef(),
          onClick: onModalOpen,
        })}
    >
      <Modal.Content>
        {actions => props.contentNode({ onClose: actions.onModalClose })}
      </Modal.Content>
    </Modal>
  );
}

export function Dropdown<T extends TSuper>(props: DropdownProps<T>) {
  const media = useMedia();

  if (props.inline) {
    return (
      <React.Fragment>
        {pipe(
          props.inlineLabel,
          O.fromNullable,
          O.map(x => <span className={cx(s.fontWeightBold)}>{x}</span>),
          O.toNullable,
        )}
        {props.contentNode({ onClose: () => {} })}
      </React.Fragment>
    );
  }

  return React.createElement(
    media.md ? DropdownDesktop : DropdownMobile,
    props,
  );
}
